import { Group, Grid, SegmentedControl } from '@mantine/core';
import { useMemo } from 'react';

import { useGetBlockedInstPageIds } from '../../api/getBlockedInstPageIds';
import { useGetPages } from '../../api/getPages';
import { InstPage, Platform } from '../../types';

import { AddPageButton } from './AddPageButton';
import { PageCard } from './PageCard/PageCard';

import { useSearchState } from '@/hooks/useSearchState';
import { Departments } from '@/types';

enum ToggleOptions {
  Active = 'Active',
  Archived = 'Archived',
}

enum ChannelOptions {
  All = 'All',
  Instagram = 'Instagram',
  Messenger = 'Messenger',
}

type PagesListProps = {
  platform: Platform;
  department: Departments;
};

export const PagesList = ({ platform, department }: PagesListProps) => {
  const [toggleValue, setToggleValue] = useSearchState('toggle', ToggleOptions.Active);
  const [channelValue, setChannelValue] = useSearchState('channel', ChannelOptions.All);
  const { data, isLoading } = useGetPages({
    platform,
    department,
    isArchived: toggleValue === ToggleOptions.Archived,
  });

  const visiblePages = useMemo(() => {
    const list = data?.pages || [];
    if (platform !== 'instagram' || channelValue === ChannelOptions.All) {
      return list;
    }
    return (list as InstPage[]).filter((page) => {
      if (channelValue === ChannelOptions.Messenger) {
        return page.integration === 'facebook-messenger';
      }
      return (
        !page.integration ||
        page.integration === 'instagram' ||
        page.integration === 'instagram-connect' ||
        page.integration === 'umnico'
      );
    });
  }, [data?.pages, platform, channelValue]);

  const sortedPages = useMemo(() => {
    if (platform !== 'instagram') {
      return visiblePages;
    }
    return [...visiblePages].sort((a, b) => Number(!a.department) - Number(!b.department));
  }, [visiblePages, platform]);

  const { data: blockedData } = useGetBlockedInstPageIds({
    config: { enabled: platform === 'instagram' },
  });
  const blockedPageIds = new Set(blockedData?.pageIds ?? []);

  return (
    <div>
      <Group mb={10} gap="sm">
        <SegmentedControl
          value={toggleValue}
          onChange={setToggleValue}
          data={Object.values(ToggleOptions)}
        />
        {platform === 'instagram' && (
          <SegmentedControl
            value={channelValue}
            onChange={setChannelValue}
            data={Object.values(ChannelOptions)}
          />
        )}
      </Group>
      <Grid columns={3}>
        {sortedPages.map((page) => (
          <PageCard
            key={page._id}
            page={page}
            platform={platform}
            isBlocked={blockedPageIds.has(page._id)}
          />
        ))}
      </Grid>
      <AddPageButton platform={platform} department={department} isLoading={isLoading} />
    </div>
  );
};
